import { useSortable } from '@dnd-kit/sortable';
import { CSS as cssDndKit } from '@dnd-kit/utilities';
import Button from '@rio-cloud/rio-uikit/Button';
import FadeExpander from '@rio-cloud/rio-uikit/FadeExpander';
import getOr from 'lodash/fp/getOr';
import isNil from 'lodash/fp/isNil';
import isUndefined from 'lodash/fp/isUndefined';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { DEFAULT_TIME_AT_STOPOVER } from '../../common/constants';
import type { Coords } from '../../common/types';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import {
    type ChargePoint,
    type Connector,
    useGetAssetDataQuery,
    useLazyGetChargingStationQuery,
} from '../../store/facade/facadeApiV3';
import {
    selectIsAutomatedPlanningMode,
    selectSuggestedAddresses,
    selectWaypointsOrder,
} from '../../store/search/searchSelectors';
import {
    type SuggestionAddress,
    type Waypoint,
    WaypointType,
    suggestedAddressesChanged,
    updateExtendedWaypoint,
    waypointAdded,
    waypointChanged,
    waypointRemoved,
} from '../../store/search/searchSlice';
import { selectVehicleProfile } from '../../store/vehicleProfile/vehicleProfileSelectors';
import { fetchAddressesWithDebounce } from '../fetchData/fetchAddresses';
import { reverseGeolocation } from '../fetchData/resolveAddress';
import { AddWaypointButton } from './AddWaypointButton';
import { ExtendedOptions, getConnectorCurrentAndVoltageByPower } from './ExtendedOptions';
import { WaypointInput } from './WaypointInput';

type WaypointListItemProps = {
    value: Waypoint;
    orderIndex: number;
    allowRemove: boolean;
    totalWaypoints: number;
};

export const getIcon = (index: number, max: number, waypoint: Waypoint) => {
    if (index === 0) {
        return 'start';
    }
    if (index === max) {
        return 'finish';
    }
    if (waypoint.type === WaypointType.CHARGING_STATION) {
        return 'filling-e-station';
    }
    if (waypoint.type === WaypointType.ADDITIONAL_STOP) {
        return 'arrow-down';
    }
    return 'arrow-down';
};

export const mapSuggestionType = (suggestionType: string) => {
    if (suggestionType === 'charging-station') {
        return WaypointType.CHARGING_STATION;
    }
    if (suggestionType === 'vehicle-location') {
        return WaypointType.VEHICLE_LOCATION;
    }
    return WaypointType.ADDITIONAL_STOP;
};

const DragHandle = () => {
    return (
        <div
            className='btn btn-muted btn-sm btn-icon-only cursor-grab padding-left-2
                        margin-left--10 width-25 align-self-center'
        >
            <span className='rioglyph rioglyph-drag-n-drop' />
        </div>
    );
};

const hasValidPosition = (position: { lat?: number; lng?: number }): position is Coords =>
    position.lat != null && position.lng != null;

export const WaypointListItem = (props: WaypointListItemProps) => {
    const { value: waypoint, orderIndex, allowRemove, totalWaypoints } = props;
    const extended = waypoint.extended;

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const isAutomatedPlanningMode = useAppSelector(selectIsAutomatedPlanningMode);
    const suggestedAddresses = useAppSelector(selectSuggestedAddresses);
    const waypointsOrder = useAppSelector(selectWaypointsOrder);
    const assetLocked = useAppSelector(selectVehicleProfile).assetLocked;
    const assetLockedId = assetLocked?.asset_id;

    const { currentData: vehicleData } = useGetAssetDataQuery(
        { id: assetLockedId ?? '' },
        { skip: isNil(assetLockedId) }
    );

    const vehiclePosition = {
        lat: vehicleData?.position?.latitude,
        lng: vehicleData?.position?.longitude,
    };

    const waypointId = waypoint.id;
    const isFirstWaypoint = waypointId === waypointsOrder[0];
    const addresses = getOr([], waypointId, suggestedAddresses);
    const filteredAddresses = isAutomatedPlanningMode
        ? addresses.filter(item => item.type !== WaypointType.CHARGING_STATION)
        : addresses;
    const isIntermediateWaypoint = waypointId !== waypointsOrder.at(0) && waypointId !== waypointsOrder.at(-1);

    const [fetchChargingStationData] = useLazyGetChargingStationQuery();

    useEffect(() => {
        // Reset state of first waypoint input and update suggestions when asset changes
        const id = waypointsOrder[0];
        let position: Coords | undefined;
        if (assetLockedId && hasValidPosition(vehiclePosition)) {
            position = vehiclePosition;
        }

        if (isFirstWaypoint) {
            // Clear 'use Vehicle Location' from WaypointInput if new selected vehicle has no position
            if (waypoint.type === WaypointType.VEHICLE_LOCATION && isNil(position)) {
                dispatch(
                    waypointChanged({
                        id,
                        address: null,
                        addressLabel: '',
                        type: WaypointType.ADDITIONAL_STOP,
                    } as Waypoint)
                );
            }

            // Update first waypoint suggestions
            const firstSuggestions = getOr([], waypointId, suggestedAddresses).filter(
                address => address.type !== WaypointType.VEHICLE_LOCATION
            );
            if (isUndefined(position)) {
                dispatch(
                    suggestedAddressesChanged({
                        addresses: firstSuggestions,
                        id,
                    })
                );
            } else {
                firstSuggestions.push({
                    type: WaypointType.VEHICLE_LOCATION,
                    title: intl.formatMessage({ id: 'intl-msg:smartRoute.tour.useVehicleLocation' }),
                    resultType: WaypointType.VEHICLE_LOCATION,
                    position,
                });
                dispatch(
                    suggestedAddressesChanged({
                        addresses: firstSuggestions,
                        id,
                    })
                );
            }
        }
    }, [vehicleData, assetLockedId]);

    const handleInputValueChanged = (id: number, address: string) => {
        handleDeleteSuggestedRoutes(id);

        dispatch(
            waypointChanged({
                id,
                addressLabel: address,
                type: WaypointType.ADDITIONAL_STOP,
            } as Waypoint)
        );

        let position: Coords | undefined;
        if (assetLockedId && hasValidPosition(vehiclePosition)) {
            position = vehiclePosition;
        }

        fetchAddressesWithDebounce(id, address, isFirstWaypoint, position, intl);
    };

    const handleDeleteSuggestedRoutes = (id: number) => {
        if (isFirstWaypoint && assetLockedId && hasValidPosition(vehiclePosition)) {
            dispatch(
                suggestedAddressesChanged({
                    addresses: [
                        {
                            type: WaypointType.VEHICLE_LOCATION,
                            title: intl.formatMessage({ id: 'intl-msg:smartRoute.tour.useVehicleLocation' }),
                            resultType: WaypointType.VEHICLE_LOCATION,
                            position: vehiclePosition,
                        },
                    ],
                    id,
                })
            );
        } else {
            dispatch(suggestedAddressesChanged({ id, addresses: [] }));
        }
    };

    const handleSuggestionSelected = async (id: number, suggestion: SuggestionAddress) => {
        handleDeleteSuggestedRoutes(id);
        if (suggestion.resultType === WaypointType.VEHICLE_LOCATION) {
            const address = await dispatch(reverseGeolocation(suggestion.position));
            suggestion.address = address;
        }

        let allConnectors: Connector[] = [];
        let current: number | null = null;
        let voltage: number | null = null;
        let maxPower: number | null = null;
        let timeAtStopover: string | null = null;

        if (mapSuggestionType(suggestion.resultType) === WaypointType.CHARGING_STATION && suggestion.station_id) {
            const stationData = await fetchChargingStationData({ id: suggestion.station_id }, true).unwrap();

            if (stationData) {
                allConnectors =
                    stationData.chargePoints?.flatMap((cp: ChargePoint) => cp?.connectors ?? []).filter(Boolean) ?? [];

                maxPower = stationData.maxPower ?? null;
                timeAtStopover = DEFAULT_TIME_AT_STOPOVER;

                if (allConnectors?.length && maxPower) {
                    const result = getConnectorCurrentAndVoltageByPower(allConnectors, maxPower);
                    current = result?.current ?? null;
                    voltage = result?.voltage ?? null;
                }
            }
        }

        const newWaypoint = {
            id,
            addressLabel: suggestion.title,
            address: suggestion.address,
            coordinates: { ...suggestion.position },
            type: mapSuggestionType(suggestion.resultType),
            station_id: suggestion.station_id,
            connectors: allConnectors,
            maxPower: maxPower,
            current,
            voltage,
            isInRange: null,
            timeAtStopover,
            cargoAtDeparture: null,
        } as Waypoint;

        dispatch(waypointChanged(newWaypoint));
    };

    const handleInputCleared = (id: number) => handleDeleteSuggestedRoutes(id);

    const handleWaypointAdd = () => dispatch(waypointAdded(waypointId));

    const handleWaypointRemove = () => dispatch(waypointRemoved(waypointId));

    const { setNodeRef, transform, transition, listeners } = useSortable({ id: waypointId });
    const style = {
        transform: cssDndKit.Transform.toString(transform),
        transition,
    };

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                {...listeners}
                className='bg-lightest rounded padding-10 display-flex align-items-baseline'
                data-testid='waypoint-container'
            >
                <DragHandle />
                <div className='flex-1-1-0'>
                    <WaypointInput
                        waypoint={waypoint}
                        suggestedAddresses={filteredAddresses}
                        onInputValueChanged={handleInputValueChanged}
                        onSuggestionSelected={handleSuggestionSelected}
                        onInputCleared={handleInputCleared}
                        onRemove={handleWaypointRemove}
                        allowRemove={allowRemove}
                        icon={getIcon(orderIndex, totalWaypoints, waypoint)}
                    />

                    <FadeExpander show={extended}>
                        <ExtendedOptions waypoint={waypoint} />
                    </FadeExpander>
                </div>
                {isIntermediateWaypoint && (
                    <div className='align-self-center'>
                        <Button
                            bsStyle='muted'
                            bsSize='sm'
                            style={{ paddingRight: 1 }}
                            onClick={() => dispatch(updateExtendedWaypoint({ waypointId, extended: !extended }))}
                        >
                            <span
                                className={`margin-0 rioglyph ${
                                    extended ? 'rioglyph-chevron-up' : 'rioglyph-chevron-down'
                                }`}
                            />
                        </Button>
                    </div>
                )}
            </div>
            <AddWaypointButton onClick={handleWaypointAdd} />
        </>
    );
};
